/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";
import clipboardCopy from "clipboard-copy"; // clipboardCopy for mail variables
import JoditEditor from "jodit-react";

/*import url and gateway methods */
import {
  getData,
  postData,
  uploadMultipleFile,
  uploadSingleFile,
} from "utils/Gateway";
import * as url from "helper/UrlHelper";

// import mail variables from json file
import mailVariables from "data/Prod/MailVariables.json";

import { getCustomStyles, reactSelectStyle } from "Config/Config"; // Assuming reactSelectStyle is available
import FileUploadField from "../FileUploadField/FileUploadField";

const ConversationNewMailPopup = ({
  reloadList,
  contactPersonEmail,
  setShowAlert,
  setAlertMessage,
  setMessageType,
  moduleSlug = "",
  subject = "",
  content = "",
  allowSmtpSelection = false,
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token"); // token

  // loggedin user information
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  // ------------ hooks for user Inputs --------------------------------
  const [mailTemplateList, setMailTemplateList] = useState([]);
  const [usermailsetting, setUsermailsetting] = useState({});

  /* ------------- Language translation imports starts here ------------- */
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [receivermails, setReceivermails] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailDescription, setMailDescription] = useState(""); //for ck editor
  // CC and BCC multi-select states (creatable)
  const [ccValue, setCcValue] = useState([]);
  const [bccValue, setBccValue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileUploadIds, setFileUploadIds] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [mailSending, setMailSending] = useState(false);
  const [linkTrackerBlock, setlinkTrackerBlock] = useState([]);
  const [emailsendingmethod, setEmailsendingmethod] = useState("user_smtp"); // 'user_smtp' or 'owner_smtp'
  const [showCcBcc, setShowCcBcc] = useState(false); // toggle for CC/BCC visibility
  const [showMailVariables, setShowMailVariables] = useState(false); // toggle for Mail Variables visibility
  const [isRefreshingMailSettings, setIsRefreshingMailSettings] =
    useState(false); // loading state for refreshing mail settings

  /* hook for validation */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  // simple email validator
  const isValidEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;
    return re.test(String(email).toLowerCase());
  };

  // handlers for creatable selects
  const handleCreateCc = (inputValue) => {
    if (isValidEmail(inputValue)) {
      const newOpt = { label: inputValue, value: inputValue };
      setCcValue((prev) => [...prev, newOpt]);
      if (errors.cc) setErrors((prev) => ({ ...prev, cc: "" }));
    } else {
      setErrors((prev) => ({ ...prev, cc: t("Please enter a valid email") }));
    }
  };

  const handleCreateBcc = (inputValue) => {
    if (isValidEmail(inputValue)) {
      const newOpt = { label: inputValue, value: inputValue };
      setBccValue((prev) => [...prev, newOpt]);
      if (errors.bcc) setErrors((prev) => ({ ...prev, bcc: "" }));
    } else {
      setErrors((prev) => ({ ...prev, bcc: t("Please enter a valid email") }));
    }
  };

  //link tracker block
  const linkTrackerTemplate = {
    link: "",
    label: "",
    referencetracker: "",
  };

  const editor = useRef(null);

  const editorCustomConfig = {
    readonly: false,
    placeholder: "",
    minHeight: 400,
    toolbarSticky: false,
    uploader: {
      insertImageAsBase64URI: false,
    },
    events: {
      afterPaste: async (event) => {
        // take the saved clipboard data
        const clipboardData = event.clipboardData || window.clipboardData;
        const items = clipboardData.items;

        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            const file = items[i].getAsFile();

            // Rename the file using a timestamp
            const timestamp = Date.now();
            const fileExtension = file.type.split("/")[1]; // Get the file extension (e.g., png, jpeg)
            const newFileName = `image-${timestamp}.${fileExtension}`;

            const renamedFile = new File([file], newFileName, {
              type: file.type,
            });

            try {
              // upload the file data in server
              const requestUrl =
                url.API_BASE_URL +
                url.API_SINGLE_FILE_UPLOAD +
                `?token=${token}`;

              const response = await uploadSingleFile(requestUrl, renamedFile);

              if (response.status) {
                const imageUrl = url.SERVER_URL + response.data.path;
                // console.log("imageUrl", imageUrl);

                // Replace blob URL with the server URL
                const editorInstance = editor.current;

                if (editorInstance) {
                  const content = editorInstance.value;
                  const regex = /<img[^>]+src="data:image\/[^">]+"/g;
                  const newContent = content.replace(regex, (match) => {
                    const base64DataMatch = match.match(/src="([^"]+)"/);
                    if (
                      base64DataMatch &&
                      base64DataMatch[1].startsWith("data:image/")
                    ) {
                      return match.replace(base64DataMatch[1], imageUrl);
                    }
                    return match;
                  });

                  // add in editor content
                  editorInstance.value = newContent;

                  // add in state content
                  setMailDescription(newContent);
                }
              }
            } catch (error) {
              console.error("Image upload failed:", error);
            }
          }
        }
      },
    },
    defaultActionOnPaste: "insert_as_html",
    defaultLineHeight: 1.5,
    enter: "div",
    statusbar: false,
    sizeLG: 900,
    sizeMD: 700,
    sizeSM: 400,
    toolbarAdaptive: false,
    disablePlugins: [
      "image",
      "video",
      "about",
      "classSpan",
      "file",
      "speechRecognize",
      "copyformat",
      "spellcheck",
      "add-new-line",
    ],
    removeButtons: ["link"],
  };

  const editorConfig = useMemo(() => editorCustomConfig, []);

  const validateForm = () => {
    const newErrors = {};

    // check if user has entered any email or not
    if (!(receivermails || "").trim()) {
      newErrors.receivermails = t("Please enter a mail address!");
    }

    // check if subject is empty or not - Fixed validation logic
    if (!mailSubject || !mailSubject.trim()) {
      newErrors.mailSubject = t("Please enter a subject!");
    }

    // check if description is empty or not
    if (
      !mailDescription ||
      !mailDescription.trim() ||
      mailDescription.trim() === "<div></div>" ||
      mailDescription.trim() === "<div><br></div>"
    ) {
      newErrors.mailDescription = t("Please enter a message!");
    }

    // validate cc and bcc entries (should not contain invalid emails)
    if (ccValue && ccValue.length > 0) {
      const invalidCc = ccValue.find((c) => !isValidEmail(c.value));
      if (invalidCc) newErrors.cc = t("Please enter a valid email");
    }
    if (bccValue && bccValue.length > 0) {
      const invalidBcc = bccValue.find((c) => !isValidEmail(c.value));
      if (invalidBcc) newErrors.bcc = t("Please enter a valid email");
    }

    // check if user is trying to send to multiple recipients without SMTP/SendGrid configuration
    if (!usermailsetting?.usesmtpmail && !usermailsetting?.usesendgrid) {
      // Count emails by splitting on common delimiters (comma, semicolon) - only check main email field
      const emailCount = receivermails
        ? receivermails.split(/[,;]/).filter((email) => email.trim()).length
        : 0;

      if (emailCount > 1) {
        newErrors.receivermails = t(
          "To send multiple mail at once, you either need to send via your own SMTP or your Sendgrid account. Please contact admin for more details.",
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //function for get all mail templates
  const getAllMailTemplates = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL + url.API_GET_TEMPLATE_LIST + `?token=${token}`;

      const response = await getData(requestUrl);

      // console.log(response);

      if (response.status) {
        const apiData = response.data.map((template) => ({
          ...template,
          label: template.label,
          value: template._id,
        }));
        setMailTemplateList(apiData);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  //function for get user mail settings
  const getUserMailSettings = async () => {
    try {
      setIsRefreshingMailSettings(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_MAIL_SETTING + `?token=${token}`;

      const response = await getData(requestUrl);

      console.log("user mail setting", response);

      if (response.status) {
        setUsermailsetting(response.data);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsRefreshingMailSettings(false);
    }
  };

  //for adding a new link tracker block
  const onAddLinkTrackerBlock = () => {
    //console.log('linkTrackerBlock >> ', linkTrackerBlock)

    setlinkTrackerBlock([...linkTrackerBlock, linkTrackerTemplate]);
  };

  //** Function for delete link tracker block */
  const onDeleteLinkTrackerBlock = (index) => {
    const filteredLinkTrackerBlock = [...linkTrackerBlock];
    filteredLinkTrackerBlock.splice(index, 1);
    setlinkTrackerBlock(filteredLinkTrackerBlock);
  };

  //** Function for change link tracker value */
  const changeLinkTrackerValue = (index, field, value) => {
    const updatedFormValues = linkTrackerBlock.map((block, i) =>
      index == i ? Object.assign(block, { [field]: value }) : block,
    );

    //console.log('updatedFormValues', updatedFormValues);
    setlinkTrackerBlock(updatedFormValues);
  };

  //** Function for mapping form repeater data */
  const updatedLinkTrackerBlock = (linkTracker = []) => {
    const linkTrackerArr = linkTracker.map((linkTrackerData) => {
      let linkTrackerSavedData = {
        link: linkTrackerData?.link,
        label: linkTrackerData?.label,
        referencetracker: linkTrackerData?._id || "",
      };

      return linkTrackerSavedData;
    });
    setlinkTrackerBlock(linkTrackerArr);
  };

  //function for file upload
  const uploadHandler = async (files) => {
    try {
      setIsUploading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_MULTIPLE_FILE_UPLOAD + `?token=${token}`;
      const response = await uploadMultipleFile(requestUrl, files);
      if (response.status) {
        /*----- assign uploaded files with path for display start ------*/
        const uploadedFileValues = response.data.map(
          ({ name, path, _id, ...other }) => ({
            name,
            path: url.SERVER_URL + path,
            _id,
          }),
        );
        //check old file exist or not if exist then new file will concat with old file
        if (uploadedFiles.length == 0) {
          setUploadedFiles(uploadedFileValues);
        } else {
          let fileArr = uploadedFiles.concat(uploadedFileValues);
          setUploadedFiles(fileArr);
        }
        /*----- assign uploaded files with path for display end ------*/
        setIsUploading(false);
        /*----- assign uploaded file ids start ------*/
        const fileIds = response.data.map(({ _id, ...other }) => ({ _id }));
        //check old file ids are exist or not.If exist then new id will concat with old id
        if (fileUploadIds.length == 0) {
          setFileUploadIds(fileIds);
        } else {
          let fileArr = fileUploadIds.concat(fileIds);
          setFileUploadIds(fileArr);
        }
        /*----- assign uploaded file ids end ------*/
        resetFile();
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  //function for delete file from upload stack
  const closeFileHandler = (index) => {
    const closedFile = [...uploadedFiles];
    const removedFileId = [...fileUploadIds];
    closedFile.splice(index, 1);
    removedFileId.splice(index, 1);
    setUploadedFiles(closedFile);
    setFileUploadIds(removedFileId);
    resetFile();
  };

  // function for clear file value
  const resetFile = () => {
    const file = document.getElementById("uploadedMailUploadFile");
    if (file) {
      file.value = null;
    } else {
      return;
    }
  };

  //function for select template
  const templateSelectionHandler = (val) => {
    // console.log(val);
    //assign template value for select
    setSelectedTemplate(val);

    //set value for send mail
    setMailSubject(val.subject);

    // setEditorContent(val.description);
    setMailDescription(val.description);

    //assing link text

    if (val.linktrackerids && val.linktrackerids.length > 0) {
      updatedLinkTrackerBlock(val.linktrackerids);
    }

    //assign uploaded files from mail template
    const uploadedFileValues = val.uploads.map(({ name, path, _id }) => ({
      name,
      path: url.SERVER_URL + path,
      _id,
    }));

    if (uploadedFiles.length == 0) {
      setUploadedFiles(uploadedFileValues);
    } else {
      let fileArr = uploadedFiles.concat(uploadedFileValues);
      setUploadedFiles(fileArr);
    }

    //assign uploaded file id for sending mail
    const fileIds = val.uploads.map(({ _id, ...other }) => ({ _id }));

    if (fileUploadIds.length == 0) {
      setFileUploadIds(fileIds);
    } else {
      let fileArr = fileUploadIds.concat(fileIds);
      setFileUploadIds(fileArr);
    }

    if (errors.mailSubject) {
      setErrors((prev) => ({ ...prev, mailSubject: "" }));
    }
  };

  /* function to add label */
  const saveMailHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to top
      }
      return;
    }

    let templateId = selectedTemplate ? selectedTemplate.value : null;

    let mailData = {
      receivermails: receivermails,
      subject: mailSubject,
      sender: currentUser._id,
      description: mailDescription,
      messagetemplate: templateId,
      linktrackers: linkTrackerBlock,
      uploads: fileUploadIds,
      moduleslug: moduleSlug,
      ccmails: ccValue && ccValue.length ? ccValue.map((c) => c.value) : [],
      bccmails: bccValue && bccValue.length ? bccValue.map((b) => b.value) : [],
      emailsendingmethod: allowSmtpSelection ? emailsendingmethod : "user_smtp",
    };

    console.log("mailData=======>", mailData);

    try {
      setMailSending(true);

      let requestUrl =
        url.API_BASE_URL + url.API_ADD_MESSAGE + `?token=${token}`;

      const response = await postData(requestUrl, mailData);

      console.log("Mail Response", response);

      setMailSending(false);

      if (response.status) {
        setMessageType("success");

        resetHandler();

        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#offcanvasNewMail"),
        ).hide();

        reloadList();
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      setMessageType("error");
      setAlertMessage(error.message);
    }

    setShowAlert(true);
  };

  /* reset */
  const resetHandler = () => {
    resetFile();
    setMailDescription("");
    setSelectedTemplate(null);
    setReceivermails("");
    setCcValue([]);
    setBccValue([]);
    setMailSubject("");
    setUploadedFiles([]);
    setlinkTrackerBlock([]);
    setFileUploadIds([]);
    setErrors({});
    setEmailsendingmethod("user_smtp");
  };

  useEffect(() => {
    getAllMailTemplates();
    getUserMailSettings();
  }, []);

  useEffect(() => {
    // Add any additional effect logic here if needed
    if (subject) setMailSubject(subject);
    if (content) setMailDescription(content);

    // Set initial receivermails from contactPersonEmail
    if (contactPersonEmail) {
      setReceivermails(contactPersonEmail);

      // Validate for multiple emails if no SMTP/SendGrid
      if (!usermailsetting?.usesmtpmail && !usermailsetting?.usesendgrid) {
        const emailCount = contactPersonEmail
          .split(/[,;]/)
          .filter((email) => email.trim()).length;

        if (emailCount > 1) {
          setErrors((prev) => ({
            ...prev,
            receivermails: t(
              "To send multiple mail at once, you either need to send via your own SMTP or your Sendgrid account. Please contact admin for more details.",
            ),
          }));
        }
      } else {
        // Clear the multiple email error if SMTP or SendGrid is now available
        setErrors((prev) => {
          if (
            prev.receivermails &&
            prev.receivermails.includes("multiple mail")
          ) {
            return { ...prev, receivermails: "" };
          }
          return prev;
        });
      }
    }

    // Clear error for receivermails field if SMTP/SendGrid becomes available and there are multiple emails
    if (usermailsetting?.usesmtpmail || usermailsetting?.usesendgrid) {
      setErrors((prev) => {
        // Only clear if the error message is about multiple emails
        if (
          prev.receivermails &&
          (prev.receivermails.includes("multiple mail") ||
            prev.receivermails.includes("SMTP or your Sendgrid"))
        ) {
          return { ...prev, receivermails: "" };
        }
        return prev;
      });
    }
  }, [contactPersonEmail, subject, content, usermailsetting]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0"
      tabIndex="-1"
      id="offcanvasNewMail"
      aria-labelledby="offcanvasNewMailLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              mail
            </span>{" "}
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasNewMailLabel"
            >
              {t("Send Mail")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {t("Compose and send new emails")}
            </p>
          </div>
        </div>
        <button
          onClick={resetHandler}
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>
      <div
        className="offcanvas-body p-4"
        ref={offcanvasBodyRef}
        style={{ paddingBottom: "80px" }}
      >
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="fields-container">
              {/* <!-- ========== Start SMTP Selection Section ========== --> */}
              {allowSmtpSelection && (
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-0 text-dark">
                      {t("Email Sending Method")}
                    </label>
                    <span
                      className="material-symbols-outlined text-primary fs-5 cursor-pointer"
                      style={{ cursor: "pointer" }}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title={t(
                        "Choose between your own SMTP/SendGrid or use the component owner's configuration",
                      )}
                    >
                      info
                    </span>
                  </div>
                  <div className="d-flex gap-4 mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="emailsendingmethod"
                        id="emailMethodUserSmtp"
                        value="user_smtp"
                        checked={emailsendingmethod === "user_smtp"}
                        onChange={(e) => setEmailsendingmethod(e.target.value)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="emailMethodUserSmtp"
                      >
                        {t("User SMTP")}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="emailsendingmethod"
                        id="emailMethodOwnerSmtp"
                        value="owner_smtp"
                        checked={emailsendingmethod === "owner_smtp"}
                        onChange={(e) => setEmailsendingmethod(e.target.value)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="emailMethodOwnerSmtp"
                      >
                        {t("Owner SMTP")}
                      </label>
                    </div>
                  </div>
                  {emailsendingmethod === "user_smtp" && (
                    <div className="row g-3">
                      <div className="col-lg-6">
                        <div className="p-3 bg-light rounded-6 border border-gray-200 h-100">
                          <h6 className="fw-semibold mb-3 text-dark fs-sm">
                            {t("How it works")}
                          </h6>
                          <div className="d-flex align-items-start gap-2 mb-2">
                            <span
                              className="material-symbols-outlined text-primary"
                              style={{ fontSize: "18px" }}
                            >
                              check_circle
                            </span>
                            <small className="text-muted">
                              {t("Send with your name & signature")}
                            </small>
                          </div>
                          <div className="d-flex align-items-start gap-2 mb-2">
                            <span
                              className="material-symbols-outlined text-primary"
                              style={{ fontSize: "18px" }}
                            >
                              check_circle
                            </span>
                            <small className="text-muted">
                              {t("Uses your SMTP/SendGrid/Platform SMTP")}
                            </small>
                          </div>
                          <div className="d-flex align-items-start gap-2">
                            <span
                              className="material-symbols-outlined text-warning"
                              style={{ fontSize: "18px" }}
                            >
                              info
                            </span>
                            <small className="text-muted">
                              {t("SMTP: 1 email/time, SendGrid: Multiple")}
                            </small>
                          </div>
                        </div>
                      </div>
                      {/* user current mail setting */}
                      <div className="col-lg-6">
                        {usermailsetting?.usesmtpmail && (
                          <div className="p-3 bg-white rounded-6 border border-primary h-100">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="fw-semibold mb-0 text-primary fs-sm d-flex align-items-center gap-1">
                                <span
                                  className="material-symbols-outlined"
                                  style={{ fontSize: "18px" }}
                                >
                                  settings
                                </span>
                                {t("Your SMTP Config")}
                              </h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-primary"
                                onClick={getUserMailSettings}
                                disabled={isRefreshingMailSettings}
                                title={t("Refresh settings")}
                              >
                                {isRefreshingMailSettings ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                ) : (
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: "18px" }}
                                  >
                                    refresh
                                  </span>
                                )}
                              </button>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">{t("Host")}:</small>
                              <small
                                className="fw-medium text-truncate ms-2"
                                style={{ maxWidth: "150px" }}
                              >
                                {usermailsetting?.smtphost || "N/A"}
                              </small>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">{t("User")}:</small>
                              <small
                                className="fw-medium text-truncate ms-2"
                                style={{ maxWidth: "150px" }}
                              >
                                {usermailsetting?.smtpuser || "N/A"}
                              </small>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">{t("Port")}:</small>
                              <small className="fw-medium">
                                {usermailsetting?.smtpport || "N/A"}
                              </small>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">
                                {t("Password")}:
                              </small>
                              <small className="fw-medium">
                                {usermailsetting?.smtppass ? "••••••••" : "N/A"}
                              </small>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">
                                {t("Sender Name")}:
                              </small>
                              <small
                                className="fw-medium text-truncate ms-2"
                                style={{ maxWidth: "150px" }}
                              >
                                {usermailsetting?.smtpsendername || "N/A"}
                              </small>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                {t("Secure")}:
                              </small>
                              <small className="fw-medium">
                                {usermailsetting?.smtpsecure
                                  ? t("Yes")
                                  : t("No")}
                              </small>
                            </div>
                          </div>
                        )}
                        {usermailsetting?.usesendgrid && (
                          <div className="p-3 bg-white rounded-6 border border-success h-100">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-start gap-2 flex-grow-1">
                                <span
                                  className="material-symbols-outlined text-success"
                                  style={{ fontSize: "20px" }}
                                >
                                  check_circle
                                </span>
                                <div>
                                  <h6 className="fw-semibold mb-1 text-success fs-sm">
                                    {t("SendGrid Enabled")}
                                  </h6>
                                  <small className="text-muted">
                                    {t(
                                      "Please ensure your SendGrid API is working correctly by sending a test mail.",
                                    )}
                                  </small>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-success flex-shrink-0"
                                onClick={getUserMailSettings}
                                disabled={isRefreshingMailSettings}
                                title={t("Refresh settings")}
                              >
                                {isRefreshingMailSettings ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                ) : (
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: "18px" }}
                                  >
                                    refresh
                                  </span>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                        {!usermailsetting?.usesmtpmail &&
                          !usermailsetting?.usesendgrid && (
                            <div className="p-3 bg-white rounded-6 border border-warning h-100">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="d-flex align-items-start gap-2 flex-grow-1">
                                  <span
                                    className="material-symbols-outlined text-warning"
                                    style={{ fontSize: "20px" }}
                                  >
                                    warning
                                  </span>
                                  <div>
                                    <h6 className="fw-semibold mb-1 text-warning fs-sm">
                                      {t("No Configuration")}
                                    </h6>
                                    <small className="text-muted">
                                      {t(
                                        "Please configure your SMTP or SendGrid settings",
                                      )}
                                    </small>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0 text-warning flex-shrink-0"
                                  onClick={getUserMailSettings}
                                  disabled={isRefreshingMailSettings}
                                  title={t("Refresh settings")}
                                >
                                  {isRefreshingMailSettings ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                  ) : (
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "18px" }}
                                    >
                                      refresh
                                    </span>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                  {emailsendingmethod === "owner_smtp" && (
                    <div className="row g-3">
                      <div className="col-lg-6">
                        <div className="p-3 bg-light rounded-6 border border-gray-200 h-100">
                          <h6 className="fw-semibold mb-3 text-dark fs-sm">
                            {t("How it works")}
                          </h6>
                          <div className="d-flex align-items-start gap-2 mb-2">
                            <span
                              className="material-symbols-outlined text-primary"
                              style={{ fontSize: "18px" }}
                            >
                              check_circle
                            </span>
                            <small className="text-muted">
                              {t("Send with owner's name & signature")}
                            </small>
                          </div>
                          <div className="d-flex align-items-start gap-2 mb-2">
                            <span
                              className="material-symbols-outlined text-primary"
                              style={{ fontSize: "18px" }}
                            >
                              check_circle
                            </span>
                            <small className="text-muted">
                              {t("Uses owner's SMTP/SendGrid")}
                            </small>
                          </div>
                          <div className="d-flex align-items-start gap-2">
                            <span
                              className="material-symbols-outlined text-warning"
                              style={{ fontSize: "18px" }}
                            >
                              info
                            </span>
                            <small className="text-muted">
                              {t("SMTP: 1 email/time, SendGrid: Multiple")}
                            </small>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="p-3 bg-white rounded-6 border border-info h-100">
                          <div className="d-flex align-items-start gap-2">
                            <span
                              className="material-symbols-outlined text-info"
                              style={{ fontSize: "20px" }}
                            >
                              corporate_fare
                            </span>
                            <div>
                              <h6 className="fw-semibold mb-1 text-info fs-sm">
                                {t("Owner Configuration")}
                              </h6>
                              <small className="text-muted">
                                {t(
                                  "Email will be sent using component owner's mail settings and signature",
                                )}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* <!-- ========== End SMTP Selection Section ========== --> */}

              {/* <!-- ========== Start mail address Section ========== --> */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="receivermails"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Mail Address")} *
                </label>
                <input
                  type="text"
                  id="receivermails"
                  className={`form-control fs-sm shadow-none ${
                    errors.receivermails
                      ? "is-invalid"
                      : "border border-gray-300"
                  }`}
                  placeholder={t("e.g., recipient@example.com")}
                  value={receivermails}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setReceivermails(newValue);

                    // Real-time validation for multiple emails without SMTP/SendGrid
                    if (
                      !usermailsetting?.usesmtpmail &&
                      !usermailsetting?.usesendgrid
                    ) {
                      const emailCount = newValue
                        ? newValue.split(/[,;]/).filter((email) => email.trim())
                            .length
                        : 0;

                      if (emailCount > 1) {
                        setErrors((prev) => ({
                          ...prev,
                          receivermails: t(
                            "To send multiple mail at once, you either need to send via your own SMTP or your Sendgrid account. Please contact admin for more details.",
                          ),
                        }));
                      } else {
                        setErrors((prev) => ({ ...prev, receivermails: "" }));
                      }
                    } else {
                      if (errors.receivermails) {
                        setErrors((prev) => ({ ...prev, receivermails: "" }));
                      }
                    }
                  }}
                />
                {errors.receivermails && (
                  <div className="invalid-feedback">{errors.receivermails}</div>
                )}

                {/* Toggle CC/BCC Link */}
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-primary text-decoration-none d-flex align-items-center gap-1 fs-sm"
                    onClick={() => setShowCcBcc(!showCcBcc)}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "18px" }}
                    >
                      {showCcBcc ? "remove" : "add"}
                    </span>
                    <span>
                      {showCcBcc ? t("Hide CC/BCC") : t("Add CC/BCC")}
                    </span>
                  </button>
                </div>
              </div>

              {/* CC Mail */}
              {showCcBcc && (
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    {t("CC Mail")}
                  </label>
                  <CreatableSelect
                    isMulti
                    className="w-100"
                    placeholder={t("Add CC emails")}
                    value={ccValue}
                    onChange={(val) => {
                      setCcValue(val || []);
                      if (errors.cc) setErrors((prev) => ({ ...prev, cc: "" }));
                    }}
                    onCreateOption={handleCreateCc}
                    styles={getCustomStyles()}
                  />
                  {errors.cc && (
                    <div className="error-message mt-2">
                      <p className="d-flex align-items-center gap-1 text-danger mb-0">
                        <span className="material-symbols-outlined">
                          warning
                        </span>
                        <span>{errors.cc}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* BCC Mail */}
              {showCcBcc && (
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    {t("BCC Mail")}
                  </label>
                  <CreatableSelect
                    isMulti
                    className="w-100"
                    placeholder={t("Add BCC emails")}
                    value={bccValue}
                    onChange={(val) => {
                      setBccValue(val || []);
                      if (errors.bcc)
                        setErrors((prev) => ({ ...prev, bcc: "" }));
                    }}
                    onCreateOption={handleCreateBcc}
                    styles={getCustomStyles()}
                  />
                  {errors.bcc && (
                    <div className="error-message mt-2">
                      <p className="d-flex align-items-center gap-1 text-danger mb-0">
                        <span className="material-symbols-outlined">
                          warning
                        </span>
                        <span>{errors.bcc}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* <!-- ========== Start mail template Section ========== --> */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor=""
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Mail Template")}
                </label>
                <Select
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  placeholder={t("Choose a pre-defined mail template")}
                  options={mailTemplateList}
                  value={selectedTemplate}
                  onChange={(val) => {
                    templateSelectionHandler(val);
                  }}
                />

                {/* Toggle Mail Variables Link */}
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-primary text-decoration-none d-flex align-items-center gap-1 fs-sm"
                    onClick={() => setShowMailVariables(!showMailVariables)}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "18px" }}
                    >
                      {showMailVariables ? "remove" : "add"}
                    </span>
                    <span>
                      {showMailVariables
                        ? t("Hide Mail Variables")
                        : t("Show Mail Variables")}
                    </span>
                  </button>
                </div>
              </div>
              {/* <!-- ========== End mail template Section ========== --> */}

              {/* <!-- ========== Start mail variable Section ========== --> */}
              {showMailVariables && (
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <div className="row align-items-center">
                    <label
                      htmlFor=""
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      {t("Mail Variable")}
                    </label>
                    <div className="col-lg-6 mb-3 mb-lg-0">
                      <Select
                        className="w-100"
                        styles={reactSelectStyle("3px", "6px", true, false)}
                        placeholder={t("Select Mail Variable")}
                        options={mailVariables}
                        onChange={(event) => {
                          clipboardCopy(event.value);
                        }}
                      />
                    </div>
                    <div className="col-lg-6">
                      <div className="mail_vairable_text border border-gray-300 field-container d-flex flex-column flex-lg-row gap-3 gap-lg-0 align-items-lg-center body-lg-bg rounded-10">
                        {t(
                          "Select the mail variable you want to add. Next just paste where you want it use it.",
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* <!-- ========== End mail variable Section ========== --> */}
              {/* <!-- ========== Start subject Section ========== --> */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="mailSubject"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Subject")} *
                </label>
                <input
                  type="text"
                  id="mailSubject"
                  placeholder={t(
                    "e.g., Important Update Regarding Your Account",
                  )}
                  className={`form-control fs-sm rounded-lg-2 shadow-none ${
                    errors.mailSubject ? "is-invalid" : "border border-gray-300"
                  }`}
                  value={mailSubject}
                  onChange={(e) => {
                    setMailSubject(e.target.value);
                    if (errors.mailSubject) {
                      setErrors((prev) => ({ ...prev, mailSubject: "" }));
                    }
                  }}
                />
                {errors.mailSubject && (
                  <div className="invalid-feedback">{errors.mailSubject}</div>
                )}
              </div>
              {/* <!-- ========== End subject Section ========== --> */}
              {/* ------- editor section start ------- */}
              <div
                className={`form-group mb-4 p-3 border rounded-8 shadow-sm bg-white ${errors.mailDescription ? "border-danger" : ""}`}
              >
                <label
                  htmlFor="mailDescription"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Description")} *
                </label>
                <JoditEditor
                  ref={editor}
                  value={mailDescription}
                  config={editorConfig}
                  onBlur={(newContent) => {
                    // console.log("New content in on blur----->", newContent);
                    setMailDescription(newContent);
                    if (
                      errors.mailDescription &&
                      newContent &&
                      newContent.trim() &&
                      newContent.trim() !== "<div></div>" &&
                      newContent.trim() !== "<div><br></div>"
                    ) {
                      setErrors((prev) => ({ ...prev, mailDescription: "" }));
                    }
                  }}
                  onChange={(newContent, event) => {
                    // setMailDescription(newContent);
                  }}
                />
                {errors.mailDescription && (
                  <div className="text-danger mt-2 fs-sm">
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px", verticalAlign: "middle" }}
                    >
                      error
                    </span>{" "}
                    {errors.mailDescription}
                  </div>
                )}
              </div>
              {/* ------- editor section end ------- */}
              {/* ========= file upload start ========== */}

              <div className="mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="Attachments"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Attachments")}
                </label>
                <FileUploadField
                  label={t("Upload Files")}
                  accept="doc mp4 pdf"
                  onUpload={uploadHandler}
                  files={uploadedFiles}
                  onRemove={closeFileHandler}
                  loading={isUploading}
                  multiple
                  fileType="document"
                  fileClassName="form-group"
                />
              </div>

              {/* ========= file upload end ===========*/}
              {/* <!-- ========== Start list of link tracker Section ========== --> */}
              {linkTrackerBlock?.length > 0 && (
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  {/* Section Header */}
                  <div className="row mb-3">
                    <div className="col">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        {t("Link Tracker")}
                      </label>
                    </div>
                  </div>

                  {/* Link Tracker Blocks */}
                  {linkTrackerBlock.map((trackerBlock, index) => (
                    <div
                      key={index}
                      className="form-group p-3 mb-3 bg-gray-500 action_link_block_bg rounded-8"
                    >
                      {/* A single flex container for the columns and the button */}
                      <div className="d-flex align-items-end gap-3">
                        {/* --- Column 1: URL to Track --- */}
                        <div className="flex-grow-1">
                          <label className="fs-sm fw-semibold text-dark d-block mb-2">
                            {t("Add URL to Track")}
                          </label>
                          <input
                            type="text"
                            name="link"
                            className="form-control bg-white border border-gray-300 w-100"
                            placeholder={t(
                              "e.g., https://yourwebsite.com/tracking",
                            )}
                            value={trackerBlock.link}
                            onChange={(e) =>
                              changeLinkTrackerValue(
                                index,
                                "link",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        {/* --- Column 2: Display Label --- */}
                        <div className="flex-grow-1">
                          <label className="fs-sm fw-semibold text-dark d-block mb-2">
                            {t("Label to Display if Link Clicked")}
                          </label>
                          <input
                            type="text"
                            name="label"
                            className="form-control bg-white border border-gray-300 w-100"
                            placeholder={t("e.g., Website Visit Tracking")}
                            value={trackerBlock.label}
                            onChange={(e) =>
                              changeLinkTrackerValue(
                                index,
                                "label",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        {/* --- Column 3: Delete Button --- */}
                        <div className="ms-2 mb-1">
                          {" "}
                          {/* Added small margin for spacing */}
                          <button
                            type="button"
                            className="btn btn-outline-danger rounded-circle shadow-sm d-flex align-items-center justify-content-center hover-scale flex-shrink-0"
                            onClick={() => onDeleteLinkTrackerBlock(index)}
                            title={t("Remove Link Tracker")}
                            style={{ width: "38px", height: "38px" }} // Adjusted size to better match input height
                          >
                            <span className="material-symbols-outlined fs-5">
                              delete
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* <!-- ========== End list of link tracker Section ========== --> */}
              {/* <!-- ========== Start add new link tracker button ========== --> */}
              {/* <div className="form-group mb-3">
              <div className="row">
                <div className="col-3">
                  <Link
                    onClick={onAddLinkTrackerBlock}
                    to="#"
                    className="d-flex align-items-center gap-1 text-primary fw-medium"
                  >
                    <span className="d-block material-symbols-outlined">add</span>
                    <span>{t("Add Link Tracker")}</span>
                  </Link>
                </div>
              </div>
            </div> */}
              {/* <!-- ========== End add new link tracker button ========== --> */}
            </div>
          </form>
        </div>
      </div>

      {/* Fixed Footer with Send Button */}
      <div
        className="offcanvas-footer p-4 border-top bg-white"
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3">
          {/* Error Display Section - Left Side */}
          <div className="flex-grow-1">
            {Object.keys(errors).length > 0 && (
              <div
                className="alert alert-warning mb-0 py-2 px-3 d-flex align-items-center gap-2"
                role="alert"
              >
                <span
                  className="material-symbols-outlined text-warning"
                  style={{ fontSize: "20px" }}
                >
                  warning
                </span>
                <span className="fs-sm fw-semibold text-warning mb-0">
                  {t("Please fix the validation errors to proceed")}
                </span>
              </div>
            )}
          </div>

          {/* Send Button - Right Side */}
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2 flex-shrink-0"
            onClick={saveMailHandler}
            style={{ cursor: mailSending ? "not-allowed" : "pointer" }}
            disabled={mailSending ? true : false}
          >
            {mailSending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("Sending...")}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined me-1">send</span>{" "}
                {t("Send Mail")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
export default ConversationNewMailPopup;
